import axios from "axios";
import { clearSessionData, redirectToLogin } from "./sessionManager";

const URL = process.env.REACT_APP_API_URL || "http://localhost:3023";

const UF_OPTIONS = [
  "AC",
  "AL",
  "AP",
  "AM",
  "BA",
  "CE",
  "DF",
  "ES",
  "GO",
  "MA",
  "MT",
  "MS",
  "MG",
  "PA",
  "PB",
  "PR",
  "PE",
  "PI",
  "RJ",
  "RN",
  "RS",
  "RO",
  "RR",
  "SC",
  "SP",
  "SE",
  "TO",
];

const T_EMP_ALIASES = {
  Baiemp: "baiemp",
  Cidemp: "cidemp",
  Estemp: "estemp",
  CEPemp: "cepemp",
  ieEmp: "ieemp",
  ultNFemp: "ultnfemp",
  JurDia: "jurdia",
  MulApv: "mulapv",
  MsnFixaNF: "msnfixanf",
  AliqInterna: "aliqinterna",
  RegEspTr: "regesptr",
  RegFisc: "regfisc",
  CRT: "crt",
  ULTNFC: "ultnfc",
  SerieNFc: "serienfc",
  ModeloNFc: "modelonfc",
  NrToken: "nrtoken",
  ConsultarFone: "consultarfone",
  ImpRecCx: "imprecx",
  ImpMPnoPV: "impmpnopv",
  IDCSC: "idcsc",
  CaminhoNFS: "caminhonfs",
  UltNFSv: "ultnfsv",
  ModNFSv: "modnfsv",
  SerNFSv: "sernfsv",
  AliqISS: "aliqiss",
  PetShop: "petshop",
  AutVendAbxQteEmb: "autvendabxqteemb",
  ControlaSaidaDaComanda: "controlasaidadacomanda",
  LerBarrasMod2: "lerbarrasmod2",
  TIPO: "tipo",
  TipoEmp: "tipoemp",
  BloqItemSemSaldoPDV: "bloqitemsemsaldopdv",
  PedirTXDeEntrega: "pedirtxdeentrega",
  ImprimirTXDeEntNoNFC: "imprimirtxdeentnonfc",
  AGENDAPORHORA: "agendaporhora",
  CaminhoDoCertificado: "caminhodocertificado",
  SenhaDoCertificado: "senhadocertificado",
  CNPJPrefeitura: "cnpjprefeitura",
  UserWeb: "userweb",
  SenhaWeb: "senhaweb",
  FraseSecWeb: "frasesecweb",
  ChaveAcessoWeb: "chaveacessoweb",
  ChaveAutorizWeb: "chaveautorizweb",
  LogoPrefeitura: "logoprefeitura",
  Prefeitura: "prefeitura",
  PathDoProvedor: "pathdoprovedor",
  LogoExpandidaSN: "logoexpandidasn",
  ComissaoPorMarkup: "comissaopormarkup",
};

const T_SOCIO_ALIASES = {
  AUTOCOD: "autocod",
  CDEMP: "cdemp",
  CDCLI: "cdcli",
  MATRICULA: "matricula",
  NATURALIDADE: "naturalidade",
  ESTADOCIVIL: "estadocivil",
  DTADMISSAO: "dtadmissao",
  TITULACAO: "titulacao",
  REGTRAB: "regtrab",
  CLASSE: "classe",
  NIVEL: "nivel",
  UNIDADE: "unidade",
  DEPARTAMENTO: "departamento",
  DTFILICAO: "dtfilicao",
  SITUACAO: "situacao",
  OPCONTRIB: "opcontrib",
  TIPOCONTA: "tipoconta",
  BANCO: "banco",
  AGENCIA: "agencia",
  DIGAGENCIA: "digagencia",
  CONTA: "conta",
  DIGCONTA: "digconta",
  CHAVEPIX: "chavepix",
  TPCHAVEPIX: "tpchavepix",
  DTAUTORIZACAO: "dtautorizacao",
  CURSO: "curso",
};

const T_BCO_ALIASES = {
  TxEmisBol: "txemisbol",
  Agencia: "agencia",
  Ambiente: "ambiente",
  CaminhoDoCert: "caminhodocert",
  Carteira: "carteira",
  Cedente: "cedente",
  ChavePIX: "chavepix",
  ClientID: "clientid",
  ClientSecret: "clientsecret",
  CodTransmissao: "codtransmissao",
  Conta: "conta",
  DigAgencia: "digagencia",
  DigCedente: "digcedente",
  DigConta: "digconta",
  DigCta2: "digcta2",
  TipoChavePIX: "tipochavepix",
  SenhaDoCert: "senhadocert",
  LAYOUT: "layout",
  PSP: "psp",
  TIPOCOB: "tipocob",
  VARCARTEIRA: "varcarteira",
};

const isPlainObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const cloneValue = (value) => {
  if (Array.isArray(value)) {
    return value.map(cloneValue);
  }
  if (isPlainObject(value)) {
    return { ...value };
  }
  return value;
};

const getPathWithoutQuery = (url = "") => {
  const [path] = url.split("?");
  return path || "";
};

const normalizePath = (url = "") => {
  const path = getPathWithoutQuery(url);
  if (!path) {
    return "";
  }
  return path.startsWith("/") ? path : `/${path}`;
};

const isPublicAuthRoute = (path = "") =>
  path.startsWith("/auth/login") || path.startsWith("/auth/password/reset-");

const getQueryString = (url = "") => {
  const [, query] = url.split("?");
  return query || "";
};

const routeRoot = (url = "") => {
  const path = normalizePath(url);
  const parts = path.split("/").filter(Boolean);
  return parts.length > 0 ? `/${parts[0]}` : path;
};

const withOriginalQuery = (path, originalUrl = "") => {
  const query = getQueryString(originalUrl);
  return query ? `${path}?${query}` : path;
};

const normalizeText = (value) =>
  typeof value === "string" ? value.trim().toUpperCase() : "";

const toNumber = (value, fallback = null) => {
  if (value === undefined || value === null || value === "") {
    return fallback;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const getSelectedCompanyId = () =>
  toNumber(localStorage.getItem("cdemp"), null);

const paginateRows = (rows, params = {}) => {
  const page = Math.max(1, toNumber(params.page, 1));
  const pageSize = Math.max(1, toNumber(params.pageSize, rows.length || 1));
  const start = (page - 1) * pageSize;
  const pagedRows = rows.slice(start, start + pageSize);

  return {
    count: rows.length,
    rows: pagedRows,
    dados: pagedRows,
  };
};

const applyResponseAliases = (record, aliases) => {
  if (!isPlainObject(record)) {
    return record;
  }

  const next = { ...record };
  Object.entries(aliases).forEach(([alias, canonical]) => {
    if (next[canonical] !== undefined && next[alias] === undefined) {
      next[alias] = next[canonical];
    }
  });
  return next;
};

const applyRequestAliases = (record, aliases) => {
  if (!isPlainObject(record)) {
    return record;
  }

  const next = { ...record };
  Object.entries(aliases).forEach(([alias, canonical]) => {
    if (next[alias] !== undefined && next[canonical] === undefined) {
      next[canonical] = next[alias];
    }
    delete next[alias];
  });
  return next;
};

const normalizeCompanyRecord = (record) =>
  applyResponseAliases(record, T_EMP_ALIASES);

const normalizeSocioRecord = (record) =>
  applyResponseAliases(record, T_SOCIO_ALIASES);

const normalizeBankRecord = (record) =>
  applyResponseAliases(record, T_BCO_ALIASES);

const normalizeRecordForRoute = (url, record) => {
  switch (routeRoot(url)) {
    case "/t_emp":
      return normalizeCompanyRecord(record);
    case "/t_socio":
      return normalizeSocioRecord(record);
    case "/t_bco":
      return normalizeBankRecord(record);
    default:
      return record;
  }
};

const filterByStatus = (rows, status, activeField) => {
  const normalizedStatus = normalizeText(status);
  if (!normalizedStatus || normalizedStatus === "TODOS") {
    return rows;
  }

  if (normalizedStatus === "ATIVOS") {
    return rows.filter(
      (row) => normalizeText(row[activeField] || row.ativo) === "S",
    );
  }

  if (normalizedStatus === "INATIVOS") {
    return rows.filter(
      (row) => normalizeText(row[activeField] || row.ativo) !== "S",
    );
  }

  return rows;
};

const filterCompanies = (rows, params = {}) => {
  const term = normalizeText(params.deemp);
  const filtered = filterByStatus(rows, params.status, "ativaemp").filter(
    (row) => {
      if (!term) return true;
      return [row.deemp, row.fantemp, row.apelido]
        .map(normalizeText)
        .some((value) => value.includes(term));
    },
  );

  return paginateRows(filtered, params);
};

const filterSuppliers = (rows, params = {}) => {
  const term = normalizeText(params.defor);
  const filtered = filterByStatus(rows, params.status, "ativofor").filter(
    (row) => {
      if (!term) return true;
      return [row.defor, row.fant, row.cidfor, row.baifor]
        .map(normalizeText)
        .some((value) => value.includes(term));
    },
  );

  return paginateRows(filtered, params);
};

const filterPlanAccounts = (rows, params = {}) => {
  const filtered = rows.filter((row) => {
    if (
      params.debcre &&
      normalizeText(row.debcre) !== normalizeText(params.debcre)
    ) {
      return false;
    }

    if (params.tipo && normalizeText(row.tipo) !== normalizeText(params.tipo)) {
      return false;
    }

    if (!params.decta) {
      return true;
    }

    return normalizeText(row.decta).includes(normalizeText(params.decta));
  });

  return paginateRows(filtered, params);
};

const filterCities = (rows, params = {}) => {
  const uf = normalizeText(params.uf);
  const filtered = rows.filter((row) => !uf || normalizeText(row.uf) === uf);
  return paginateRows(filtered, params);
};

const filterBanks = (rows, params = {}) => {
  const term = normalizeText(params.deban);
  const bankCode = normalizeText(params.cdban);
  const filtered = filterByStatus(rows, params.status, "ativo").filter((row) => {
    if (bankCode && normalizeText(row.cdban) !== bankCode) {
      return false;
    }

    if (!term) {
      return true;
    }

    return normalizeText(row.deban).includes(term);
  });

  return paginateRows(filtered, params);
};

const filterSocios = (rows, params = {}) => {
  const clientCode = toNumber(params.cliente, null);
  const filtered = rows.filter((row) => {
    if (clientCode === null) {
      return true;
    }
    return toNumber(row.cdcli ?? row.CDCLI, -1) === clientCode;
  });

  return paginateRows(filtered, params);
};

const filterRowsBySelectedCompany = (rows, companyFields = ["cdemp", "CDEMP"]) => {
  const selectedCompanyId = getSelectedCompanyId();
  if (selectedCompanyId === null) {
    return rows;
  }

  return rows.filter((row) => {
    const rowCompanyId = companyFields
      .map((field) => toNumber(row[field], null))
      .find((value) => value !== null);

    return rowCompanyId === null || rowCompanyId === selectedCompanyId;
  });
};

const buildSyntheticResponse = (config, data) =>
  Promise.resolve({
    data,
    status: 200,
    statusText: "OK",
    headers: {},
    config,
    request: { synthetic: true },
  });

const transport = axios.create({
  baseURL: URL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

transport.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  const warehouse = localStorage.getItem("cdemp");

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  if (warehouse) {
    config.headers["x-warehouse"] = warehouse;
  }

  return config;
});

const api = axios.create({
  baseURL: URL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    const warehouse = localStorage.getItem("cdemp");
    const path = normalizePath(config.url);
    const method = (config.method || "get").toLowerCase();

    config.metadata = {
      originalUrl: config.url || "",
      ...config.metadata,
    };

    if (isPublicAuthRoute(path)) {
      delete config.headers.Authorization;
      delete config.headers["x-warehouse"];
    } else {
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      if (warehouse) {
        config.headers["x-warehouse"] = warehouse;
      }
    }

    if (path === "/t_uf" && method === "get") {
      const rows = UF_OPTIONS.map((UF) => ({ UF }));
      config.adapter = () =>
        buildSyntheticResponse(config, {
          count: rows.length,
          rows,
          dados: rows,
        });
      return config;
    }

    if (/^\/t_emp\/user\/[^/]+$/i.test(path)) {
      config.metadata.legacyRoute = "companies_user";
      config.url = withOriginalQuery("/auth/companies_user", config.url);
      return config;
    }

    if (
      /^\/t_for\/[^/]+$/i.test(path) &&
      !/^\/t_for\/[^/]+\/[^/]+$/i.test(path)
    ) {
      const [, , supplierId] = path.split("/");
      const cdemp = localStorage.getItem("cdemp") || "1";
      config.url = withOriginalQuery(`/t_for/${cdemp}/${supplierId}`, config.url);
    }

    if (method === "put" && /^\/t_/i.test(path)) {
      config.method = "patch";
    }

    if (isPlainObject(config.data)) {
      const payload = cloneValue(config.data);

      switch (routeRoot(path)) {
        case "/t_emp":
          config.data = applyRequestAliases(payload, T_EMP_ALIASES);
          break;
        case "/t_socio": {
          const normalized = applyRequestAliases(payload, T_SOCIO_ALIASES);
          const localCompanyId = toNumber(localStorage.getItem("cdemp"), null);
          if (normalized.cdemp === undefined && localCompanyId !== null) {
            normalized.cdemp = localCompanyId;
          }
          config.data = normalized;
          break;
        }
        case "/t_bco":
          config.data = applyRequestAliases(payload, T_BCO_ALIASES);
          break;
        default:
          break;
      }
    }

    return config;
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  async (response) => {
    const originalUrl =
      response.config.metadata?.originalUrl || response.config.url || "";
    const originalPath = normalizePath(originalUrl);
    const actualPath = normalizePath(response.config.url || "");
    const params = response.config.params || {};

    if (response.config.metadata?.legacyRoute === "companies_user") {
      const rows = (Array.isArray(response.data) ? response.data : []).map(
        normalizeCompanyRecord,
      );
      response.data = {
        count: rows.length,
        rows,
        dados: rows,
      };
      return response;
    }

    if (Array.isArray(response.data)) {
      const rows = response.data.map((record) =>
        normalizeRecordForRoute(actualPath, record),
      );

      switch (routeRoot(actualPath)) {
        case "/t_emp":
          response.data = filterCompanies(rows, params);
          return response;
        case "/t_cli":
          response.data = paginateRows(
            filterRowsBySelectedCompany(rows),
            params,
          );
          return response;
        case "/t_for":
          response.data = filterSuppliers(rows, params);
          return response;
        case "/t_placon":
          response.data = filterPlanAccounts(rows, params);
          return response;
        case "/t_cid":
          response.data = filterCities(rows, params);
          return response;
        case "/t_bco":
          response.data = filterBanks(rows, params);
          return response;
        case "/t_socio":
          response.data = filterSocios(
            filterRowsBySelectedCompany(rows, ["cdemp", "CDEMP"]),
            params,
          );
          return response;
        case "/t_bai": {
          if (!params.cidade) {
            response.data = paginateRows(rows, params);
            return response;
          }

          try {
            const cityResponse = await transport.get("/t_cid");
            const cityRows = Array.isArray(cityResponse.data)
              ? cityResponse.data
              : cityResponse.data?.rows || [];
            const matchingCityIds = new Set(
              cityRows
                .filter(
                  (city) =>
                    normalizeText(city.decid) === normalizeText(params.cidade),
                )
                .map((city) => toNumber(city.autocod, null))
                .filter((value) => value !== null),
            );

            const filtered = rows.filter((row) =>
              matchingCityIds.has(toNumber(row.codcid, null)),
            );
            response.data = paginateRows(filtered, params);
            return response;
          } catch (error) {
            response.data = paginateRows([], params);
            return response;
          }
        }
        default:
          response.data = paginateRows(rows, params);
          return response;
      }
    }

    if (isPlainObject(response.data)) {
      response.data = normalizeRecordForRoute(actualPath, response.data);
    }

    if (originalPath === "/auth/companies_user" && Array.isArray(response.data)) {
      response.data = response.data.map(normalizeCompanyRecord);
    }

    return response;
  },
  (error) => {
    const path = normalizePath(error.config?.url || "");
    if (
      error.response &&
      error.response.status === 401 &&
      !isPublicAuthRoute(path)
    ) {
      clearSessionData();
      redirectToLogin();
    }
    return Promise.reject(error);
  },
);

export default api;
